import React, { PureComponent } from 'react';

import { injectIntl, InjectedIntl, FormattedMessage } from 'react-intl';

import { Checkbox } from '@atlaskit/checkbox';
import Tooltip from '@atlaskit/tooltip';
import { RelativeDate, relativeDateString } from '@atlassian/bitkit-date';

import { BranchDivergence } from 'src/components/branch-divergence';
import { differenceInDays } from 'src/components/date/utils/time';
import { BylineSection, TabletByline } from 'src/components/pageable-table';
import {
  UnmatchedCommitAuthorAvatar,
  UnmatchedCommitAuthorName,
} from 'src/components/user-profile';
import { UserAvatar } from 'src/containers/user-avatar';
import { UserName } from 'src/containers/user-name';
import { BranchingModelState } from 'src/redux/branches/reducers/branch-list-reducer';
import { shouldAllowBulkDelete } from 'src/redux/branches/utils';
import BranchKindLozenges from 'src/sections/repository/sections/branches/containers/branch-kind-lozenges';
import { CenteredRow, SubtleLink } from 'src/styles';

import BranchesBuildStatus from '../../containers/branch-build-status';
import { DetailedBranch } from '../../types';
import BranchPullRequests from '../branch-pull-requests';

import { BranchActions } from './branch-actions';
import messages from './branch-row.i18n';
import * as styles from './branch-row.styled';

type Props = {
  mainBranch: DetailedBranch;
  branch: DetailedBranch;
  isFocused: boolean;
  isLoading: boolean;
  isSelected: boolean;
  staleFilterSelected: boolean;
  onToggleBranchSelect: (branch: DetailedBranch) => void;
  isInSelectionMode: boolean;
  branchingModel: BranchingModelState;
  intl: InjectedIntl;
};

type State = {
  isActionMenuOpen: boolean;
};

class BranchRow extends PureComponent<Props, State> {
  branchLinkRef: HTMLAnchorElement | null | undefined;

  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    isLoading: false,
  };

  state = {
    isActionMenuOpen: false,
  };

  componentDidUpdate(prevProps: Props) {
    if (!this.branchLinkRef) {
      return;
    }

    if (!prevProps.isFocused && this.props.isFocused) {
      this.branchLinkRef.focus();
      return;
    }

    if (prevProps.isFocused && !this.props.isFocused) {
      this.branchLinkRef.blur();
    }
  }

  renderAuthorName = () => {
    const { raw, user } = this.props.branch.target.author;

    if (!user) {
      return <UnmatchedCommitAuthorName rawCommitAuthor={raw} />;
    }

    return (
      <UserName
        linkComponent={SubtleLink}
        profileCardPosition="bottom-start"
        user={user}
      />
    );
  };

  renderBranchName = () => {
    const { branch, staleFilterSelected, isLoading } = this.props;
    const {
      target: { date },
      ahead,
    } = branch;
    return (
      <styles.BranchName>
        <Tooltip content={branch.name}>
          <styles.BranchLink href={branch.links.html.href}>
            {branch.name}
          </styles.BranchLink>
        </Tooltip>
        <BranchKindLozenges
          branchName={branch.name}
          daysOld={differenceInDays(date)}
          merged={ahead === 0}
          isLoading={isLoading}
          staleFilterSelected={staleFilterSelected}
        />
      </styles.BranchName>
    );
  };

  renderBranchDivergence = () => {
    const { branch, mainBranch } = this.props;
    const { ahead, behind } = branch;
    const isAvailable = ahead !== null && behind !== null;
    return (
      <CenteredRow>
        <BranchDivergence
          commitsAhead={ahead}
          commitsBehind={behind}
          isAvailable={isAvailable}
          mainBranchName={mainBranch.name}
        />
      </CenteredRow>
    );
  };

  renderBranchTarget = () => {
    const {
      branch: { target, name: branchName },
      isLoading,
      intl,
    } = this.props;

    const avatar = target.author.user ? (
      <UserAvatar
        isDisabled={isLoading}
        profileCardPosition="bottom-end"
        size="small"
        user={target.author.user}
      />
    ) : (
      <UnmatchedCommitAuthorAvatar
        size="small"
        rawCommitAuthor={target.author.raw}
      />
    );

    return (
      <styles.BranchTarget>
        {avatar}
        <styles.TargetLink
          href={target.links.html.href}
          aria-label={intl.formatMessage(messages.dateText, {
            branchName,
            relativeDate: relativeDateString(target.date, intl),
          })}
        >
          <RelativeDate date={target.date} />
        </styles.TargetLink>
      </styles.BranchTarget>
    );
  };

  renderBranchActions = () => {
    const { branch, branchingModel } = this.props;
    const { isActionMenuOpen } = this.state;
    return (
      <CenteredRow>
        <BranchActions
          allowBulkDelete={shouldAllowBulkDelete(branch, branchingModel)}
          branch={branch}
          isOpen={isActionMenuOpen}
          onOpenChange={isOpen => this.setState({ isActionMenuOpen: isOpen })}
        />
      </CenteredRow>
    );
  };

  handleBranchSelectionChange = () =>
    this.props.onToggleBranchSelect(this.props.branch);

  render() {
    const { branch, branchingModel, isFocused, isSelected, isInSelectionMode } =
      this.props;

    return (
      <styles.TableRow isFocused={isFocused} isMainBranch={branch.isMainBranch}>
        {isInSelectionMode && (
          <styles.TableCell>
            <Checkbox
              isDisabled={!shouldAllowBulkDelete(branch, branchingModel)}
              isChecked={isSelected}
              onChange={this.handleBranchSelectionChange}
            />
          </styles.TableCell>
        )}
        <styles.TableCell>
          {this.renderBranchName()}
          <TabletByline>
            <BylineSection>
              <FormattedMessage {...messages.byline} />{' '}
              {this.renderAuthorName()}
            </BylineSection>
            <BylineSection>
              <SubtleLink href={branch.target.links.html.href}>
                <RelativeDate date={branch.target.date} />
              </SubtleLink>
            </BylineSection>
          </TabletByline>
        </styles.TableCell>

        <styles.BranchDivergenceTableCell colSpan={2}>
          {!branch.isMainBranch && this.renderBranchDivergence()}
        </styles.BranchDivergenceTableCell>

        <styles.TargetTableCell>
          {this.renderBranchTarget()}
        </styles.TargetTableCell>

        <styles.PullRequestTableCell>
          <CenteredRow>
            <BranchPullRequests branch={branch} />
          </CenteredRow>
        </styles.PullRequestTableCell>

        <styles.TableCell align="center">
          <CenteredRow>
            <BranchesBuildStatus branch={branch} />
          </CenteredRow>
        </styles.TableCell>

        <styles.TableCell align="center">
          {this.renderBranchActions()}
        </styles.TableCell>
      </styles.TableRow>
    );
  }
}

export default injectIntl(BranchRow);
